import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "./../NcdForm.css";
import Loader from "../../../../components/Loader";
import { ncdAPI } from "../../../../services/ncd.api";
import { formatToApiDate, formatToInputDate } from "../../../../helpers/common-helper";
import { Button, FormControl, MenuItem, Box, Grid, Typography, Select as MuiSelect, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

// Import Tab Subcomponents
import { BreastExaminationTab } from "./BreastExaminationTab";
import { BreastDiagnosisTab } from "./BreastDiagnosisTab";
import { BreastReviewTab } from "./BreastReviewTab";
import { BreastFollowupTab } from "./BreastFollowupTab";
import { BreastReferTab } from "./BreastReferTab";
import { FACILITY_MAP, getFacilityLabel } from "../../utils/constants";


export const BreastScreening = ({ individualId, breastScreeningId, viewMode }) => {

    const loggedData = JSON.parse(localStorage.getItem("loggedinUserdetails")) || JSON.parse(localStorage.getItem("NCD_loggedinUserdetails")) || {};
    const today = new Date().toISOString().split("T")[0];
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

    const [formData, setFormData] = useState({
        assessmentDate: today,
        examinationComments: null,
        exam: {
            examDate: '',
            noAbnormalityFound: false,
            shapeSizeChangeInBreast: false,
            nippleLevelNotSame: false,
            skinEdema: false,
            isLessSkinEdma: false,
            isMoreSkinEdma: false,
            lymphadenopathy: false,
            axillary: false,
            supraclavicular: false,
            infraclavicular: false,
            nodeAxillary: null,
            nodeSupraclavicular: null,
            examLocationPoints: {
                lumpPoints: [],
                ulcerPoints: [],
                nippleRetractionPoints: [],
                nippleDischargePoints: [],
                dimplingPoints: [],
                retractionSkinPoints: []
            },
            dimpling: null,
            skinRetraction: null,
            ulceration: null,
            nipplesRetraction: null,
            lump: false,
            discharge: null
        }
    });

    const [activeDot, setActiveDot] = useState(null);
    const [dotData, setDotData] = useState({});
    const [initialDotData, setInitialDotData] = useState({});
    const [initialExamLocationPoints, setInitialExamLocationPoints] = useState(null);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

    // Tab workflows
    const [activeWorkflowTab, setActiveWorkflowTab] = useState(0);

    // Modal states for updating screening status
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [modalDate, setModalDate] = useState(today);
    const [modalStatus, setModalStatus] = useState("");
    const [modalFacility, setModalFacility] = useState("");
    const [modalVerified, setModalVerified] = useState(false);
    const [modalErrors, setModalErrors] = useState({});
    const [screeningStatus, setScreeningStatus] = useState("");
    const [followupHistory, setFollowupHistory] = useState([]);
    const [medicines, setMedicines] = useState([
        { medicine: '', frequency: '', duration: '', quantity: '', instruction: '' }
    ]);
    const [medicineOptions, setMedicineOptions] = useState([]);
    const [medicineSearchText, setMedicineSearchText] = useState('');
    const [encounterId, setEncounterId] = useState(null);
    const [isDiagnosisSaved, setIsDiagnosisSaved] = useState(false);

    const statusOptions = [
        { value: "Treatement", label: "Treatement" },
        { value: "Diagnosed", label: "Diagnosed" }
    ];

    const facilityOptions = [
        { value: "CHC", label: "CHC" },
        { value: "DH", label: "DH" },
        { value: "Tertiary", label: "Tertiary" }
    ];

    const validateModalForm = () => {
        const errors = {};
        if (!modalDate) {
            errors.date = "Date is required";
        }
        if (!modalStatus) {
            errors.status = "Status is required";
        }
        if (!modalFacility) {
            errors.facility = "Facility is required";
        }
        setModalErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSaveModalStatus = async () => {
        if (!validateModalForm()) {
            toast.error("Please fill in all required fields.");
            return;
        }

        const payload = {
            changeFacility: modalFacility,
            changeStatus: modalStatus,
            statusVerified: modalVerified,
            consultationDate: formatToApiDate(modalDate)
        };

        try {
            setIsPageLoading(true);
            const response = await ncdAPI.updateSreeningStatus(200, individualId, breastScreeningId, payload);
            if (response && response.success) {
                setScreeningStatus(modalStatus);
                toast.success(`Screening status updated to "${modalStatus}" successfully!`);
                setIsStatusModalOpen(false);
            } else {
                toast.error(response?.message || "Failed to update screening status");
            }
        } catch (error) {
            console.error("updateSreeningStatus error:", error);
            toast.error(error.response?.data?.message || "Something went wrong while updating screening status");
        } finally {
            setIsPageLoading(false);
        }
    };

    const handleOpenUpdateModal = () => {
        setModalDate(formData.assessmentDate || today);
        setModalStatus(screeningStatus);
        setModalFacility("");
        setModalVerified(false);
        setModalErrors({});
        setIsStatusModalOpen(true);
    };

    // Diagnosis form fields
    const [diagnosisAction, setDiagnosisAction] = useState("Refer");
    const [doctorsObservation, setDoctorsObservation] = useState("");
    const [isDiagnosed, setIsDiagnosed] = useState(false);
    const [patientDiagnosis, setPatientDiagnosis] = useState("");
    const [isNad, setIsNad] = useState(false);
    const [referralRecommended, setReferralRecommended] = useState(false);
    const [managementRecommended, setManagementRecommended] = useState(false);
    const [isActionAlertDialogOpen, setIsActionAlertDialogOpen] = useState(false);
    const [actionTakenTitle, setActionTakenTitle] = useState("Action to be taken");
    const [actionTakenText, setActionTakenText] = useState("");

    // Review / Follow Up form fields
    const [reviewObservations, setReviewObservations] = useState("");
    const [treatmentType, setTreatmentType] = useState("");
    const [treatmentOther, setTreatmentOther] = useState("");
    const [referralRemarks, setReferralRemarks] = useState("");


    // Follow Up form fields
    const [followupReason, setFollowupReason] = useState("");
    const [followupTo, setFollowupTo] = useState("");
    const [durationDays, setDurationDays] = useState("0");
    const [durationWeeks, setDurationWeeks] = useState("0");
    const [durationMonths, setDurationMonths] = useState("0");
    const [dueDate, setDueDate] = useState("");
    const [errors, setErrors] = useState({});

    // Refer form fields
    const [referDate, setReferDate] = useState(today);
    const [referTo, setReferTo] = useState("");
    const [referReason, setReferReason] = useState("");
    const [provisionalDiagnosis, setProvisionalDiagnosis] = useState("");
    const [referredSpecialty, setReferredSpecialty] = useState("");
    const [seekingPrivateCare, setSeekingPrivateCare] = useState("");

    // DH / CHC Diagnosis state & facility check
    const facilityType = loggedData.facilityType || (loggedData.facilityTypeId === 3400 ? "DH" : loggedData.facilityTypeId === 3300 ? "CHC" : "PHC");
    const isDhOrChc = facilityType === "DH" || facilityType === "CHC" || loggedData.facilityTypeId === 3400 || loggedData.facilityTypeId === 3300;

    const [dhDiagnosisData, setDhDiagnosisData] = useState({
        typeOfDiagnosis: 'Provisional',
        diagnosis: '',
        diagnosisOther: '',
        diagnosisCancerType: '',
        diagnosisCancerTypeOther: '',
        cancerStage: null
    });
    const [dhErrors, setDhErrors] = useState({});

    // Calculate next visit date based on days/weeks/months
    const calculateDueDate = (days, weeks, months) => {
        const baseDateStr = formData?.assessmentDate || breastData?.data?.exam?.assessmentDate;
        let date = baseDateStr ? dayjs(baseDateStr, ['DD-MM-YYYY', 'YYYY-MM-DD']) : dayjs();
        if (!date.isValid()) date = dayjs();
        if (days && !isNaN(days)) date = date.add(parseInt(days), 'day');
        if (weeks && !isNaN(weeks)) date = date.add(parseInt(weeks) * 7, 'day');
        if (months && !isNaN(months)) date = date.add(parseInt(months), 'month');
        return date.format('DD-MM-YYYY');
    };

    const handleDurationChange = (type, val) => {
        let days = "0";
        let weeks = "0";
        let months = "0";
        if (type === 'days') {
            days = val;
            setDurationDays(val);
            setDurationWeeks("0");
            setDurationMonths("0");
        } else if (type === 'weeks') {
            weeks = val;
            setDurationWeeks(val);
            setDurationDays("0");
            setDurationMonths("0");
        } else if (type === 'months') {
            months = val;
            setDurationMonths(val);
            setDurationDays("0");
            setDurationWeeks("0");
        }

        // Clear errors for the modified field
        if (errors.duration || errors[`duration${type.charAt(0).toUpperCase() + type.slice(1)}`]) {
            setErrors(prev => ({
                ...prev,
                duration: null,
                [`duration${type.charAt(0).toUpperCase() + type.slice(1)}`]: null
            }));
        }

        const d = parseInt(days) || 0;
        const w = parseInt(weeks) || 0;
        const m = parseInt(months) || 0;

        if (d === 0 && w === 0 && m === 0) {
            setDueDate("");
        } else {
            const calculated = calculateDueDate(days, weeks, months);
            setDueDate(calculated);
            if (errors.dueDate) {
                setErrors(prev => ({ ...prev, dueDate: null }));
            }
        }
    };

    const validateFollowupForm = () => {
        const newErrors = {};
        if (!followupReason || !followupReason.trim()) {
            newErrors.followupReason = "Reason for Follow-up is required";
        }
        if (!followupTo || followupTo === "" || followupTo === "None") {
            newErrors.followupTo = "Follow-up facility is required";
        }

        const days = parseInt(durationDays) || 0;
        const weeks = parseInt(durationWeeks) || 0;
        const months = parseInt(durationMonths) || 0;

        if (days < 0) newErrors.durationDays = "Days cannot be negative";
        if (weeks < 0) newErrors.durationWeeks = "Weeks cannot be negative";
        if (months < 0) newErrors.durationMonths = "Months cannot be negative";

        if (days === 0 && weeks === 0 && months === 0) {
            newErrors.duration = "At least one duration (days/weeks/months) is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateReferralForm = () => {
        const newErrors = {};
        if (!referTo || referTo === "") {
            newErrors.referTo = "Referred To is required";
        }
        if (!referReason || referReason === "") {
            newErrors.referReason = "Reason for Referral is required";
        }
        if (!referredSpecialty || referredSpecialty === "") {
            newErrors.referredSpecialty = "Referred Specialty is required";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const axillaryOptions = [
        { value: 'Select All', label: 'Select All' },
        { value: 'Normal', label: 'Normal' },
        { value: 'Free', label: 'Free' },
        { value: 'Matted', label: 'Matted' },
        { value: 'Attached to skin', label: 'Attached to skin' },
        { value: 'Single', label: 'Single' },
        { value: 'Multiple', label: 'Multiple' }
    ];

    const supraclavicularOptions = [
        { value: 'All', label: 'Select All' },
        { value: 'Normal', label: 'Normal' },
        { value: 'Free', label: 'Free' },
        { value: 'Matted', label: 'Matted' },
        { value: 'Attached to skin', label: 'Attached to skin' },
        { value: 'Single', label: 'Single' },
        { value: 'Multiple', label: 'Multiple' }
    ];

    const [breastData, setBreastData] = useState(null);

    useEffect(() => {
        const transformApiToDotData = (examLocationPoints) => {
            const updatedDotData = {};

            if (examLocationPoints.lumpPoints) {
                examLocationPoints.lumpPoints.forEach((point) => {
                    const dotId = convertApiIdToDotId(point.id);
                    if (dotId) {
                        updatedDotData[dotId] = {
                            type: 'lump',
                            ...point.attributes,
                        };
                    }
                });
            }
            if (examLocationPoints.ulcerPoints) {
                examLocationPoints.ulcerPoints.forEach((point) => {
                    const dotId = convertApiIdToDotId(point.id);
                    if (dotId) {
                        updatedDotData[dotId] = {
                            type: 'ulcer',
                            ...point.attributes,
                        };
                    }
                });
            }
            if (examLocationPoints.nippleRetractionPoints) {
                examLocationPoints.nippleRetractionPoints.forEach((point) => {
                    const dotId = convertApiIdToDotId(point.id);
                    if (dotId) {
                        updatedDotData[dotId] = {
                            type: 'nippleRetraction',
                            ...point.attributes,
                        };
                    }
                });
            }
            if (examLocationPoints.nippleDischargePoints) {
                examLocationPoints.nippleDischargePoints.forEach((point) => {
                    const dotId = convertApiIdToDotId(point.id);
                    if (dotId) {
                        updatedDotData[dotId] = {
                            type: 'nippleDischarge',
                            ...point.attributes,
                        };
                    }
                });
            }
            if (examLocationPoints.dimplingPoints) {
                examLocationPoints.dimplingPoints.forEach((point) => {
                    const dotId = convertApiIdToDotId(point.id);
                    if (dotId) {
                        updatedDotData[dotId] = {
                            type: 'dimpling',
                            ...point.attributes,
                        };
                    }
                });
            }
            if (examLocationPoints.retractionSkinPoints) {
                examLocationPoints.retractionSkinPoints.forEach((point) => {
                    const dotId = convertApiIdToDotId(point.id);
                    if (dotId) {
                        updatedDotData[dotId] = {
                            type: 'retractionSkin',
                            ...point.attributes,
                        };
                    }
                });
            }

            return updatedDotData;
        };

        const convertApiIdToDotId = (apiId) => {
            if (!apiId) return null;
            if (apiId === "RB-center" || apiId === "RB13") return "left-center";
            if (apiId === "LB-center" || apiId === "LB13") return "right-center";
            const match = apiId.match(/^(R|L)B(\d{1,2})$/);
            if (!match) return null;

            const side = match[1] === 'R' ? 'left' : 'right';
            const number = match[2];
            if (number === "13") return `${side}-center`;
            return `${side}-dot${number}`;
        };

        const fetchData = async () => {
            try {
                setIsPageLoading(true);
                const responseObj = await ncdAPI.getBreastScreening(individualId, breastScreeningId);
                const responseData = responseObj?.data || responseObj;

                const respaction = await ncdAPI.actionDetails(
                    200,
                    individualId,
                    breastScreeningId
                );

                if (respaction?.data?.curActName) {
                    setScreeningStatus(respaction?.data?.curActName);
                }

                if (responseData && responseData.exam) {
                    if (responseData.exam.examLocationPoints) {
                        const newDotData = transformApiToDotData(responseData.exam.examLocationPoints);
                        setDotData(newDotData);
                        setInitialDotData(newDotData);
                        setInitialExamLocationPoints(JSON.parse(JSON.stringify(responseData.exam.examLocationPoints)));
                    }

                    const formattedAssessmentDate = formatToInputDate(responseData.assessmentDate) || today;

                    setFormData({
                        assessmentDate: formattedAssessmentDate,
                        examinationComments: responseData.examinationComments || null,
                        exam: {
                            examDate: responseData.exam.examDate || '',
                            noAbnormalityFound: responseData.exam.noAbnormalityFound || false,
                            shapeSizeChangeInBreast: responseData.exam.shapeSizeChangeInBreast || false,
                            nippleLevelNotSame: responseData.exam.nippleLevelNotSame || false,
                            skinEdema: responseData.exam.skinEdema || false,
                            isLessSkinEdma: responseData.exam.isLessSkinEdma || false,
                            isMoreSkinEdma: responseData.exam.isMoreSkinEdma || false,
                            lymphadenopathy: responseData.exam.lymphadenopathy || false,
                            axillary: responseData.exam.axillary || false,
                            supraclavicular: responseData.exam.supraclavicular || false,
                            infraclavicular: responseData.exam.infraclavicular || false,
                            nodeAxillary: responseData.exam.nodeAxillary || null,
                            nodeSupraclavicular: responseData.exam.nodeSupraclavicular || null,
                            examLocationPoints: {
                                lumpPoints: responseData.exam.examLocationPoints?.lumpPoints || [],
                                ulcerPoints: responseData.exam.examLocationPoints?.ulcerPoints || [],
                                nippleRetractionPoints: responseData.exam.examLocationPoints?.nippleRetractionPoints || [],
                                nippleDischargePoints: responseData.exam.examLocationPoints?.nippleDischargePoints || [],
                                dimplingPoints: responseData.exam.examLocationPoints?.dimplingPoints || [],
                                retractionSkinPoints: responseData.exam.examLocationPoints?.retractionSkinPoints || []
                            },
                            dimpling: responseData.exam.dimpling || null,
                            skinRetraction: responseData.exam.skinRetraction || null,
                            ulceration: responseData.exam.ulceration || null,
                            nipplesRetraction: responseData.exam.nipplesRetraction || null,
                            lump: responseData.exam.lump || false,
                            discharge: responseData.exam.discharge || null
                        }
                    });
                }

                setBreastData(responseData);
                const hasDiag = responseData?.optedToReview || responseData?.optedToRefer || responseData?.optedForManagement || responseData?.isDiagnosed;
                if (hasDiag) {
                    setIsDiagnosisSaved(true);
                }

                // Fetch screening diagnosis on initial load
                try {
                    const respDiag = await ncdAPI.getScreeningDiagnosis(200, individualId, breastScreeningId);
                    if (respDiag?.data) {
                        if (respDiag.data.docObservations) {
                            setDoctorsObservation(respDiag.data.docObservations);
                        }
                        if (respDiag.data.diagnosis) {
                            const diag = respDiag.data.diagnosis;
                            setDhDiagnosisData({
                                typeOfDiagnosis: diag.typeOfDiagnosis || (diag.provisionalDiagnosis === true ? 'Provisional' : diag.provisionalDiagnosis === false ? 'Confirmed' : 'Provisional'),
                                diagnosis: diag.diagnosisType || diag.diagnosis || '',
                                diagnosisOther: diag.diagnosisOther || '',
                                diagnosisCancerType: diag.diagnosisCancerType || '',
                                diagnosisCancerTypeOther: diag.diagnosisCancerTypeOther || '',
                                cancerStage: diag.cancerStage || null
                            });
                        }
                    }
                } catch (error) {
                    console.error("Failed to fetch initial screening diagnosis:", error);
                }

                // Fetch follow-up history
                try {
                    const historyResponse = await ncdAPI.getScreeningFollowup(200, individualId, breastScreeningId);
                    if (historyResponse) {
                        setFollowupHistory(historyResponse.data || (Array.isArray(historyResponse) ? historyResponse : []));
                    }
                } catch (error) {
                    console.error("Failed to fetch follow-up history:", error);
                }

                // Fetch screening treatment to load existing medicines
                try {
                    const treatmentResponse = await ncdAPI.getScreeningTreatment(200, individualId, breastScreeningId);
                    if (treatmentResponse && treatmentResponse.success && treatmentResponse.data) {
                        const medicalTreatment = treatmentResponse.data.treatment && Array.isArray(treatmentResponse.data.treatment)
                            ? treatmentResponse.data.treatment.find(t => Array.isArray(t.medicineMangementMedList) && t.medicineMangementMedList.length > 0)
                            : null;
                        const savedMeds = (treatmentResponse.data.medicines && treatmentResponse.data.medicines.length > 0)
                            ? treatmentResponse.data.medicines
                            : (medicalTreatment ? medicalTreatment.medicineMangementMedList : []);

                        if (savedMeds && savedMeds.length > 0) {
                            setMedicines(savedMeds.map(med => ({
                                medicine: med.name || med.drug || '',
                                frequency: med.freq || med.frequency || '',
                                duration: med.dur !== undefined && med.dur !== null ? String(med.dur) : (med.durationDays || med.duration || ''),
                                quantity: med.quan !== undefined && med.quan !== null ? String(med.quan) : (med.quantity || ''),
                                instruction: med.spInst || med.specialInstruction || med.instruction || ''
                            })));
                        }
                    }
                } catch (error) {
                    console.error("Failed to fetch screening treatment:", error);
                }

                setIsPageLoading(false);
            } catch (err) {
                setIsPageLoading(false);
                console.error("Failed to fetch and populate form data", err);
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [breastScreeningId, individualId]);

    useEffect(() => {
        if (activeWorkflowTab === 1 && individualId && breastScreeningId) {
            const fetchDiagAndAction = async () => {
                try {
                    const respDiag = await ncdAPI.getScreeningDiagnosis(200, individualId, breastScreeningId);
                    if (respDiag && (respDiag.success || respDiag.data || respDiag.diagnosis)) {
                        const diagData = respDiag.data || respDiag;
                        const diagnosed = !!diagData.isDiagnosed;
                        setIsDiagnosed(diagnosed);

                        const isNadFlag = !!diagData.isNad;
                        const referralRecFlag = !!diagData.referralRecommended;
                        const managementRecFlag = !!diagData.managementRecommended;

                        setIsNad(isNadFlag);
                        setReferralRecommended(referralRecFlag);
                        setManagementRecommended(managementRecFlag);

                        if (diagData.diagnosisRecommendationText) {
                            setActionTakenTitle("Action to be taken");
                            setActionTakenText(diagData.diagnosisRecommendationText);
                            setIsActionAlertDialogOpen(true);
                        }

                        if (diagData.docObservations) {
                            setDoctorsObservation(diagData.docObservations);
                        }

                        if (!isDhOrChc) {
                            if (!diagnosed) {
                                if (isNadFlag) {
                                    setDiagnosisAction("NAD");
                                } else if (referralRecFlag) {
                                    setDiagnosisAction("Refer");
                                } else if (managementRecFlag) {
                                    setDiagnosisAction("Management");
                                }
                            } else {
                                setDiagnosisAction("FollowUp");
                                if (diagData.diagnosis) {
                                    setPatientDiagnosis(typeof diagData.diagnosis === 'string' ? diagData.diagnosis : (diagData.diagnosis.diagnosis || diagData.diagnosis.diagnosisType || ''));
                                }
                            }
                        } else if (diagData.diagnosis) {
                            const diag = diagData.diagnosis;
                            setDhDiagnosisData({
                                typeOfDiagnosis: diag.typeOfDiagnosis || (diag.provisionalDiagnosis === true ? 'Provisional' : diag.provisionalDiagnosis === false ? 'Confirmed' : 'Provisional'),
                                diagnosis: diag.diagnosisType || diag.diagnosis || '',
                                diagnosisOther: diag.diagnosisOther || '',
                                diagnosisCancerType: diag.diagnosisCancerType || '',
                                diagnosisCancerTypeOther: diag.diagnosisCancerTypeOther || '',
                                cancerStage: diag.cancerStage || null
                            });
                        }
                    }
                } catch (err) {
                    console.error("Failed to fetch breast screening diagnosis:", err);
                }

                try {
                    const respAction = await ncdAPI.actionDetails(200, individualId, breastScreeningId);
                    if (respAction?.data?.curActName) {
                        setScreeningStatus(respAction.data.curActName);
                    }
                } catch (err) {
                    console.error("Failed to fetch action details:", err);
                }
            };

            fetchDiagAndAction();
        }
    }, [activeWorkflowTab, individualId, breastScreeningId, isDhOrChc]);

    useEffect(() => {
        const fetchMedicineOptions = async () => {
            if (medicineSearchText && medicineSearchText.length >= 1) {
                try {
                    const response = await ncdAPI.getInitialAssessmentMedicines(medicineSearchText);
                    if (response && response.data) {
                        setMedicineOptions(response.data);
                    }
                } catch (error) {
                    console.error('Error fetching medicine options:', error);
                }
            } else {
                setMedicineOptions([]);
            }
        };

        const debounceTimer = setTimeout(() => {
            fetchMedicineOptions();
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [medicineSearchText]);

    useEffect(() => {
        const frequencyMap = {
            'Once a day': 1,
            'Twice a day': 2,
            'Thrice a day': 3,
            'Four times a day': 4
        };

        let updated = false;
        const newMedicines = medicines.map(med => {
            if (med.frequency && med.duration) {
                const freqValue = frequencyMap[med.frequency] || 0;
                const durationValue = parseInt(med.duration) || 0;
                const calculatedQuantity = freqValue * durationValue;

                if (calculatedQuantity > 0 && med.quantity !== String(calculatedQuantity)) {
                    updated = true;
                    return { ...med, quantity: String(calculatedQuantity) };
                }
            } else if (med.quantity !== '') {
                updated = true;
                return { ...med, quantity: '' };
            }
            return med;
        });

        if (updated) {
            setMedicines(newMedicines);
        }
    }, [medicines]);

    const handleMedicineFieldChange = (index, field, value) => {
        const updated = [...medicines];
        updated[index][field] = value;
        setMedicines(updated);
    };

    const addMedicineRow = () => {
        setMedicines([...medicines, { medicine: '', frequency: '', duration: '', quantity: '', instruction: '' }]);
    };

    const removeMedicineRow = (index) => {
        if (medicines.length === 1) {
            setMedicines([{ medicine: '', frequency: '', duration: '', quantity: '', instruction: '' }]);
        } else {
            setMedicines(medicines.filter((_, i) => i !== index));
        }
    };

    const getSymptomType = (val) => {
        if (val === "Lump") return "lump";
        if (val === "Ulcer") return "ulcer";
        if (val === "Retraction of Nipple") return "nippleRetraction";
        if (val === "Nipple Discharge") return "nippleDischarge";
        if (val === "Skin Dimpling Puckering") return "dimpling";
        if (val === "Retraction of Skin") return "retractionSkin";
        return val ? val.toLowerCase() : "";
    };

    const handleDotClick = (dotId, e) => {
        if (formData.exam.noAbnormalityFound) return;
        e.stopPropagation();

        if (selectedValue === "Nipple Discharge" || selectedValue === "Retraction of Nipple") {
            if (dotId !== "left-center" && dotId !== "right-center") {
                return;
            }
        }

        const isLeft = dotId.startsWith("left");
        const dotNumberMatch = dotId.match(/\d+/);
        const number = dotNumberMatch ? dotNumberMatch[0] : (dotId.includes("center") ? "center" : "0");
        const formattedId = (number === "center" || number === "13") ? (isLeft ? "RB13" : "LB13") : `${isLeft ? "RB" : "LB"}${number}`;

        const typeMap = {
            "Lump": "lumpPoints",
            "Ulcer": "ulcerPoints",
            "Retraction of Nipple": "nippleRetractionPoints",
            "Nipple Discharge": "nippleDischargePoints",
            "Skin Dimpling Puckering": "dimplingPoints",
            "Retraction of Skin": "retractionSkinPoints",
        };
        const pointType = typeMap[selectedValue];
        if (!pointType) return;

        if (selectedValue === "Lump" || selectedValue === "Nipple Discharge") {
            const rect = e.target.getBoundingClientRect();
            setActiveDot(dotId);
            setDropdownPos({ top: rect.top - 100, left: rect.left - 250 });
        } else {
            const symptomType = getSymptomType(selectedValue);
            const alreadySelected = Boolean(dotData[dotId] && dotData[dotId].type === symptomType);

            if (alreadySelected) {
                setDotData(prev => {
                    const copy = { ...prev };
                    delete copy[dotId];
                    return copy;
                });
                setFormData(prev => {
                    const updatedPoints = (prev.exam.examLocationPoints[pointType] || []).filter(p => p.id !== formattedId);
                    return {
                        ...prev,
                        exam: {
                            ...prev.exam,
                            nipplesRetraction: selectedValue === "Retraction of Nipple" ? (updatedPoints.length > 0 ? true : null) : prev.exam.nipplesRetraction,
                            examLocationPoints: {
                                ...prev.exam.examLocationPoints,
                                [pointType]: updatedPoints
                            }
                        }
                    };
                });
            } else {
                setDotData(prev => ({
                    ...prev,
                    [dotId]: { type: symptomType }
                }));
                setFormData(prev => {
                    const newEntry = {
                        id: formattedId
                    };
                    const updatedPoints = [...(prev.exam.examLocationPoints[pointType] || []), newEntry];
                    return {
                        ...prev,
                        exam: {
                            ...prev.exam,
                            nipplesRetraction: selectedValue === "Retraction of Nipple" ? true : prev.exam.nipplesRetraction,
                            examLocationPoints: {
                                ...prev.exam.examLocationPoints,
                                [pointType]: updatedPoints
                            }
                        }
                    };
                });
            }
        }
    };

    const handleGlobalClick = () => setActiveDot(null);

    const [selectedValue, setSelectedValue] = useState('Lump');

    const handleResetFindings = () => {
        setSelectedValue('Lump');
        if (initialExamLocationPoints) {
            setDotData(JSON.parse(JSON.stringify(initialDotData || {})));
            setFormData(prev => ({
                ...prev,
                exam: {
                    ...prev.exam,
                    examLocationPoints: JSON.parse(JSON.stringify(initialExamLocationPoints))
                }
            }));
        } else {
            setDotData({});
            setFormData(prev => ({
                ...prev,
                exam: {
                    ...prev.exam,
                    examLocationPoints: {
                        lumpPoints: [],
                        ulcerPoints: [],
                        nippleRetractionPoints: [],
                        nippleDischargePoints: [],
                        dimplingPoints: [],
                        retractionSkinPoints: []
                    }
                }
            }));
        }
    };

    React.useEffect(() => {
        const listener = (e) => {
            const { dotId, data, type } = e.detail;
            const currentSelectedValue = type || selectedValue;
            const symptomType = getSymptomType(currentSelectedValue);

            // Save to local dotData
            setDotData((prev) => ({ ...prev, [dotId]: { type: symptomType, ...data } }));
            setActiveDot(null);

            // Determine left or right side (LB / RB)
            const isLeft = dotId.startsWith("left");
            const dotNumberMatch = dotId.match(/\d+/);
            const number = dotNumberMatch ? dotNumberMatch[0] : (dotId.includes("center") ? "center" : "0");
            const formattedId = (number === "center" || number === "13") ? (isLeft ? "RB13" : "LB13") : `${isLeft ? "RB" : "LB"}${number}`;

            // Determine which field to push into
            const typeMap = {
                "Lump": "lumpPoints",
                "Ulcer": "ulcerPoints",
                "Retraction of Nipple": "nippleRetractionPoints",
                "Nipple Discharge": "nippleDischargePoints",
                "Skin Dimpling Puckering": "dimplingPoints",
                "Retraction of Skin": "retractionSkinPoints",
            };

            const pointType = typeMap[currentSelectedValue];

            if (!pointType) return;

            let attributes = {};
            if (currentSelectedValue === "Lump") {
                attributes = {
                    consistency: data.selectedOption1,
                    fixity: data.selectedOption2,
                    size: data.textValue,
                };
            } else if (currentSelectedValue === "Nipple Discharge") {
                const dType = data.Discharge || data.dischargeType || data.selectedOption1 || "";
                attributes = {
                    Discharge: dType,
                    OtherDisc: dType === "Other" ? (data.OtherDisc || data.dischargeOther || data.textValue || "") : ""
                };
            } else {
                attributes = data;
            }

            const newEntry = currentSelectedValue === "Retraction of Nipple" ? {
                id: formattedId
            } : {
                id: formattedId,
                attributes
            };

            // Update formData
            setFormData((prev) => {
                const currentPoints = prev.exam.examLocationPoints[pointType] || [];
                const filteredPoints = currentPoints.filter(p => p.id !== formattedId);
                const updatedPoints = [...filteredPoints, newEntry];

                return {
                    ...prev,
                    exam: {
                        ...prev.exam,
                        discharge: currentSelectedValue === "Nipple Discharge" ? (data.dischargeType || data.selectedOption1) : prev.exam.discharge,
                        examLocationPoints: {
                            ...prev.exam.examLocationPoints,
                            [pointType]: updatedPoints,
                        },
                    },
                };
            });
        };

        window.addEventListener("dotDataSaved", listener);
        window.addEventListener("click", handleGlobalClick);
        return () => {
            window.removeEventListener("dotDataSaved", listener);
            window.removeEventListener("click", handleGlobalClick);
        };
    }, [selectedValue]);

    const [hoveredDot, setHoveredDot] = useState(null);

    const renderHoverTooltip = (dotId, dotInfo) => {
        if (!dotInfo) return null;

        const typeMap = {
            'lump': 'Lump',
            'ulcer': 'Ulcer',
            'nippleRetraction': 'Retraction of Nipple',
            'nippleDischarge': 'Nipple Discharge',
            'dimpling': 'Skin Dimpling',
            'retractionSkin': 'Retraction of Skin'
        };

        const typeLabel = typeMap[dotInfo.type] || 'Lump';
        const consistency = dotInfo.consistency || dotInfo.selectedOption1 || '';
        const fixity = dotInfo.fixity || dotInfo.selectedOption2 || '';
        const size = dotInfo.size || dotInfo.textValue || '';

        const details = [];
        if (dotInfo.type === 'nippleDischarge') {
            const dType = dotInfo.dischargeType || dotInfo.selectedOption1 || '';
            const dOther = dotInfo.dischargeOther || dotInfo.textValue || '';
            if (dType) details.push(dType === 'Other' && dOther ? `Other (${dOther})` : dType);
        } else {
            if (consistency) details.push(consistency);
            if (fixity) details.push(fixity);
            if (size) details.push(`${size} mm`);
        }

        const detailsText = details.join(', ');

        return (
            <div
                style={{
                    position: 'absolute',
                    bottom: 'calc(100% + 8px)',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#e2e8f0',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    padding: '5px 9px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#1e293b',
                    whiteSpace: 'normal',
                    width: 'max-content',
                    maxWidth: '170px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    pointerEvents: 'auto',
                    textAlign: 'left',
                    lineHeight: 1.3
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <span>{typeLabel}{detailsText ? `: ${detailsText}` : ''}{' '}</span>
                <div
                    style={{
                        position: 'absolute',
                        bottom: '-6px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 0,
                        height: 0,
                        borderLeft: '6px solid transparent',
                        borderRight: '6px solid transparent',
                        borderTop: '6px solid #e2e8f0'
                    }}
                />
            </div>
        );
    };

    const getCombinedDotData = () => {
        const convertApiIdToDotId = (apiId) => {
            if (!apiId) return null;
            if (apiId === "RB-center" || apiId === "RB13") return "left-center";
            if (apiId === "LB-center" || apiId === "LB13") return "right-center";
            const match = apiId.match(/^(R|L)B(\d{1,2})$/);
            if (!match) return null;
            const side = match[1] === 'R' ? 'left' : 'right';
            const number = match[2];
            if (number === "13") return `${side}-center`;
            return `${side}-dot${number}`;
        };

        const apiDots = {};
        const examPoints = formData?.exam?.examLocationPoints;
        if (examPoints) {
            const mapping = [
                { points: examPoints.lumpPoints, type: 'lump' },
                { points: examPoints.ulcerPoints, type: 'ulcer' },
                { points: examPoints.nippleRetractionPoints, type: 'nippleRetraction' },
                { points: examPoints.nippleDischargePoints, type: 'nippleDischarge' },
                { points: examPoints.dimplingPoints, type: 'dimpling' },
                { points: examPoints.retractionSkinPoints, type: 'retractionSkin' }
            ];

            mapping.forEach(({ points, type }) => {
                if (Array.isArray(points)) {
                    points.forEach(point => {
                        const dotId = convertApiIdToDotId(point.id);
                        if (dotId) {
                            apiDots[dotId] = {
                                type,
                                ...(point.attributes || {})
                            };
                        }
                    });
                }
            });
        }

        return { ...apiDots, ...dotData };
    };

    const combinedDotData = getCombinedDotData();

    const renderDots = (side) => {
        const isCenterOnlySymptom = selectedValue === "Nipple Discharge" || selectedValue === "Retraction of Nipple";
        return Array.from({ length: 12 }, (_, i) => {
            const dotId = `${side}-dot${i + 1}`;
            const dotInfo = combinedDotData[dotId];
            const isSelected = dotInfo && (!selectedValue || dotInfo.type === getSymptomType(selectedValue));
            const isHovered = hoveredDot === dotId;

            return (
                <div
                    key={dotId}
                    className={`${isSelected ? `dot dot${i + 1} selected` : `dot dot${i + 1}`} `}
                    style={{
                        pointerEvents: isCenterOnlySymptom ? 'none' : 'auto',
                        opacity: isCenterOnlySymptom ? 0.5 : 1,
                        cursor: isCenterOnlySymptom ? 'not-allowed' : 'pointer'
                    }}
                    onMouseEnter={() => !isCenterOnlySymptom && setHoveredDot(dotId)}
                    onMouseLeave={() => !isCenterOnlySymptom && setHoveredDot(null)}
                    onClick={(e) => handleDotClick(dotId, e)}
                >
                    {i + 1}
                    {!isCenterOnlySymptom && isHovered && dotInfo && renderHoverTooltip(dotId, dotInfo)}
                </div>
            );
        });
    };

    const handleConfirmNo = () => {
        setOpenConfirmDialog(false);
        setFormData(prev => ({
            ...prev,
            exam: {
                ...prev.exam,
                noAbnormalityFound: false
            }
        }));
    };

    const handleConfirmYes = () => {
        setOpenConfirmDialog(false);
        setDotData({});
        setFormData(prev => ({
            ...prev,
            exam: {
                ...prev.exam,
                noAbnormalityFound: true,
                shapeSizeChangeInBreast: false,
                nippleLevelNotSame: false,
                skinEdema: false,
                isLessSkinEdma: false,
                isMoreSkinEdma: false,
                lymphadenopathy: false,
                axillary: false,
                supraclavicular: false,
                infraclavicular: false,
                nodeAxillary: null,
                nodeSupraclavicular: null,
                examLocationPoints: {
                    lumpPoints: [],
                    ulcerPoints: [],
                    nippleRetractionPoints: [],
                    nippleDischargePoints: [],
                    dimplingPoints: [],
                    retractionSkinPoints: []
                },
                dimpling: null,
                skinRetraction: null,
                ulceration: null,
                nipplesRetraction: null,
                discharge: null
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            assessmentDate: formatToApiDate(formData.assessmentDate),
            examinationComments: formData.examinationComments || null,
            exam: {
                examDate: formatToApiDate(formData.assessmentDate),
                noAbnormalityFound: formData.exam.noAbnormalityFound,
                shapeSizeChangeInBreast: formData.exam.shapeSizeChangeInBreast,
                nippleLevelNotSame: formData.exam.nippleLevelNotSame,
                skinEdema: formData.exam.skinEdema,
                isLessSkinEdma: formData.exam.isLessSkinEdma,
                isMoreSkinEdma: formData.exam.isMoreSkinEdma,
                lymphadenopathy: formData.exam.lymphadenopathy,
                axillary: formData.exam.axillary,
                supraclavicular: formData.exam.supraclavicular,
                infraclavicular: formData.exam.infraclavicular,
                nodeAxillary: formData.exam.nodeAxillary,
                nodeSupraclavicular: formData.exam.nodeSupraclavicular,
                examLocationPoints: {
                    lumpPoints: formData.exam.examLocationPoints.lumpPoints,
                    ulcerPoints: formData.exam.examLocationPoints.ulcerPoints,
                    nippleRetractionPoints: formData.exam.examLocationPoints.nippleRetractionPoints,
                    nippleDischargePoints: formData.exam.examLocationPoints.nippleDischargePoints,
                    dimplingPoints: formData.exam.examLocationPoints.dimplingPoints,
                    retractionSkinPoints: formData.exam.examLocationPoints.retractionSkinPoints
                },
                dimpling: (formData.exam.examLocationPoints.dimplingPoints && formData.exam.examLocationPoints.dimplingPoints.length > 0) ? true : null,
                skinRetraction: (formData.exam.examLocationPoints.retractionSkinPoints && formData.exam.examLocationPoints.retractionSkinPoints.length > 0) ? true : null,
                ulceration: (formData.exam.examLocationPoints.ulcerPoints && formData.exam.examLocationPoints.ulcerPoints.length > 0) ? true : null,
                nipplesRetraction: (formData.exam.examLocationPoints.nippleRetractionPoints && formData.exam.examLocationPoints.nippleRetractionPoints.length > 0) ? true : null,
                lump: (formData.exam.examLocationPoints.lumpPoints && formData.exam.examLocationPoints.lumpPoints.length > 0) ? true : null,
                discharge: (formData.exam.examLocationPoints.nippleDischargePoints && formData.exam.examLocationPoints.nippleDischargePoints.length > 0) ? true : null
            }
        };

        try {
            setIsPageLoading(true);
            const response = await ncdAPI.submitBreastScreening(individualId, breastScreeningId, payload);

            if (response.success) {
                toast.success(`Breast cancer screening data has saved successfully`);
                const newEncounterId = response.data?.encounterId;
                if (newEncounterId) {
                    setEncounterId(newEncounterId);
                }
                setActiveWorkflowTab(1);
                setIsPageLoading(false);
            } else {
                toast.error(response.message || "Failed to save data", { className: "custom-toast-error" });
                setIsPageLoading(false);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Something went wrong", { className: "custom-toast-error" });
            setIsPageLoading(false);
        }
    };

    const handleSaveFollowup = async () => {
        if (!validateFollowupForm()) {
            toast.error("Please fill in the mandatory fields correctly.");
            return;
        }

        const loggedData = JSON.parse(localStorage.getItem("loggedinUserdetails")) || {};
        const fromFacility = Number(loggedData.facilityId || loggedData.facilityTypeId) || 3200;
        const toFacility = Number(followupTo) || FACILITY_MAP[followupTo] || fromFacility;

        const followUpPayload = {
            followUpDueDate: dueDate ? (dayjs(dueDate, 'DD-MM-YYYY', true).isValid() ? dueDate : dayjs(dueDate).format('DD-MM-YYYY')) : '',
            followUpDuration: `${durationDays || 0};${durationWeeks || 0};${durationMonths || 0}`,
            followUpReason: followupReason,
            fromFacility: fromFacility,
            toFacility: toFacility,
            type: "E",
            fromUser: loggedData.username || localStorage.getItem("ncd_username") || "doc_1220004",
            encounterId: encounterId || breastData?.encounterId || breastData?.data?.encounterId || null
        };

        try {
            setIsPageLoading(true);
            const response = await ncdAPI.submitBreastFollowup(individualId, breastScreeningId, followUpPayload);
            if (response && response.success) {
                toast.success(response.message || "Follow-up details saved successfully!");
                
                // Fetch updated history
                try {
                    const historyResponse = await ncdAPI.getScreeningFollowup(200, individualId, breastScreeningId);
                    if (historyResponse) {
                        setFollowupHistory(historyResponse.data || (Array.isArray(historyResponse) ? historyResponse : []));
                    }
                } catch (error) {
                    console.error("Failed to fetch updated follow-up history:", error);
                }
                setActiveWorkflowTab(0); // return to main tab
                // Reset form fields
                setFollowupReason("");
                setFollowupTo("");
                setDurationDays("0");
                setDurationWeeks("0");
                setDurationMonths("0");
                setDueDate("");
                setErrors({});
            } else {
                toast.error(response?.message || "Failed to save follow-up details", { className: "custom-toast-error" });
            }
        } catch (error) {
            console.error("submitBreastFollowup error:", error);
            toast.error(error.response?.data?.message || "Something went wrong while saving follow-up details", { className: "custom-toast-error" });
        } finally {
            setIsPageLoading(false);
        }
    };

    const handleSaveReferral = async () => {
        if (!validateReferralForm()) {
            toast.error("Please fill in the mandatory Referral fields (*)");
            return;
        }

        const loggedData = JSON.parse(localStorage.getItem("loggedinUserdetails")) || {};
        const referredFromFacility = Number(loggedData.facilityId || loggedData.facilityTypeId) || 3200;
        const referredToFacility = referTo ? (FACILITY_MAP[referTo] || String(referTo)) : "3400";

        const payload = {
            referredDate: referDate ? dayjs(referDate, 'YYYY-MM-DD').format('DD-MM-YYYY') : '',
            referredToFacility: referredToFacility,
            referredFromFacility: referredFromFacility,
            referralReason: referReason,
            referredSpeciality: referredSpecialty,
            privateHospitalCare: seekingPrivateCare === "Yes",
            provisionalDiagnosis: provisionalDiagnosis || null,
            encounterId: null
        };

        try {
            setIsPageLoading(true);
            const response = await ncdAPI.submitBreastRefer(individualId, breastScreeningId, payload);
            if (response && response.success) {
                toast.success(response.message || "Referral details saved successfully!");
                setActiveWorkflowTab(0); // return to main tab
                // Reset form fields
                setReferTo("");
                setReferReason("");
                setProvisionalDiagnosis("");
                setReferredSpecialty("");
                setSeekingPrivateCare("");
                setErrors({});
            } else {
                toast.error(response?.message || "Failed to save referral details", { className: "custom-toast-error" });
            }
        } catch (error) {
            console.error("submitBreastRefer error:", error);
            toast.error(error.response?.data?.message || "Something went wrong while saving referral details", { className: "custom-toast-error" });
        } finally {
            setIsPageLoading(false);
        }
    };

    const handleSaveDiagnosis = async () => {
        if (isDhOrChc) {
            const newErrors = {};
            if (!dhDiagnosisData.typeOfDiagnosis) {
                newErrors.typeOfDiagnosis = "This field is required.";
            }
            if (!dhDiagnosisData.diagnosis) {
                newErrors.diagnosis = "This field is required.";
            }
            if (Object.keys(newErrors).length > 0) {
                setDhErrors(newErrors);
                toast.error("Please fill all required fields");
                return;
            }
            setDhErrors({});
        }

        const payload = isDhOrChc ? {
            assessmentDate: formatToApiDate(formData.assessmentDate),
            optedToReview: null,
            optedToRefer: null,
            diagnosis: {
                diagnosisDate: formatToApiDate(formData.assessmentDate),
                provisionalDiagnosis: dhDiagnosisData.typeOfDiagnosis === 'Provisional',
                diagnosisType: dhDiagnosisData.diagnosis,
                diagnosisOther: dhDiagnosisData.diagnosisOther || "",
                diagnosisCancerType: dhDiagnosisData.diagnosisCancerType || "",
                diagnosisCancerTypeOther: dhDiagnosisData.diagnosisCancerTypeOther || "",
                cancerStage: dhDiagnosisData.cancerStage || null
            },
            docObservations: doctorsObservation || null,
            encounterId: encounterId || breastData?.encounterId || breastData?.data?.encounterId || null
        } : {
            assessmentDate: formatToApiDate(formData.assessmentDate),
            optedToReview: diagnosisAction === "Review" ? true : null,
            optedToRefer: diagnosisAction === "Refer" ? true : null,
            optedForManagement: diagnosisAction === "Management" ? true : null,
            diagnosis: null,
            docObservations: doctorsObservation || null,
            encounterId: encounterId || breastData?.encounterId || breastData?.data?.encounterId || null
        };

        try {
            setIsPageLoading(true);
            const response = await ncdAPI.submitScreeningDiagnosis(200, individualId, breastScreeningId, payload);
            if (response && response.success) {
                toast.success("Diagnosis action updated successfully!");
                setIsDiagnosisSaved(true);
                setActiveWorkflowTab(2); // proceed to Review / Follow Up
            } else {
                toast.error(response?.message || "Failed to save diagnosis details");
            }
        } catch (error) {
            console.error("submitScreeningDiagnosis error:", error);
            toast.error(error.response?.data?.message || "Something went wrong while saving diagnosis details");
        } finally {
            setIsPageLoading(false);
        }
    };

    const handleSaveReview = async (customPayload) => {
        const activeEncounterId = encounterId || breastData?.encounterId || breastData?.data?.encounterId || null;

        if (isDhOrChc && customPayload) {
            if (!customPayload.encounterId) {
                customPayload.encounterId = activeEncounterId;
            }
            try {
                setIsPageLoading(true);
                const response = await ncdAPI.submitScreeningTreatment(200, individualId, breastScreeningId, customPayload);
                if (response && response.success) {
                    toast.success(response.message || "Review details saved successfully!");
                    setActiveWorkflowTab(0);
                } else {
                    toast.error(response?.message || "Failed to save review details", { className: "custom-toast-error" });
                }
            } catch (error) {
                console.error("submitScreeningTreatment review error:", error);
                toast.error(error.response?.data?.message || "Something went wrong while saving review details", { className: "custom-toast-error" });
            } finally {
                setIsPageLoading(false);
            }
            return;
        }

        if (referTo || referReason) {
            if (!referTo) {
                toast.error("Please select Refer to facility");
                return;
            }
            if (!referReason) {
                toast.error("Please select Reason for Referral");
                return;
            }
        }

        const loggedData = JSON.parse(localStorage.getItem("loggedinUserdetails")) || {};
        const fromFacility = Number(loggedData.facilityId || loggedData.facilityTypeId) || 3200;
        const referredToFacility = Number(referTo) || FACILITY_MAP[referTo] || 3400;

        const referPayload = (referTo || referReason) ? {
            referredDate: dayjs(formData.assessmentDate).format('DD-MM-YYYY'),
            referredToFacility: referredToFacility,
            referredFromFacility: fromFacility,
            referralReason: referReason,
            referredSpeciality: referredSpecialty || "Medical",
            privateHospitalCare: seekingPrivateCare === "Yes",
            provisionalDiagnosis: reviewObservations || referralRemarks || null,
            encounterId: activeEncounterId
        } : null;

        const payload = {
            assessmentDate: formatToApiDate(formData.assessmentDate),
            encounterId: activeEncounterId,
            treatment: null,
            refer: referPayload,
            followUp: null,
            reviewComments: reviewObservations || null,
            medicines: medicines.filter(med => med.medicine).length > 0 ? medicines.filter(med => med.medicine).map(med => ({
                conditn: "Breast Cancer",
                name: med.medicine,
                prscrbdStrngth: null,
                freq: med.frequency,
                quan: Number(med.quantity) || null,
                dur: Number(med.duration) || null,
                durType: null,
                spInst: med.instruction
            })) : null
        };

        try {
            setIsPageLoading(true);
            const response = await ncdAPI.submitScreeningTreatment(200, individualId, breastScreeningId, payload);
            if (response && response.success) {
                toast.success(response.message || "Review details saved successfully!");
                setActiveWorkflowTab(0); // return to main tab
                setReferTo("");
                setReferReason("");
                setReferralRemarks("");
                setReviewObservations("");
                setMedicines([{ medicine: '', frequency: '', duration: '', quantity: '', instruction: '' }]);
            } else {
                toast.error(response?.message || "Failed to save review details", { className: "custom-toast-error" });
            }
        } catch (error) {
            console.error("submitScreeningTreatment review error:", error);
            toast.error(error.response?.data?.message || "Something went wrong while saving review details", { className: "custom-toast-error" });
        } finally {
            setIsPageLoading(false);
        }
    };


    const handletopRadioChange = (event) => {
        const { id } = event.target;
        setSelectedValue(id);

        setFormData((prev) => ({
            ...prev,
            exam: {
                ...prev.exam,
                lump: id === "Lump",
            },
        }));

    }

    const handleRadioChange = (event) => {
        // setSelectedValue(event.target.id);
        const { name, value } = event.target;

        setFormData((prev) => ({
            ...prev,
            exam: {
                ...prev.exam,
                [name]: value === "true" ? true : false,
            },
        }));
    };

    const handleSkinEdemaTypeChange = (e) => {
        const { value } = e.target;

        setFormData((prev) => ({
            ...prev,
            exam: {
                ...prev.exam,
                isLessSkinEdma: value === "less",
                isMoreSkinEdma: value === "more"
            }
        }));
    };



    return (
        <>
            {isPageLoading && <Loader />}

            {/* Top Tab Switchers */}
            <Box sx={{ borderBottom: '1px solid #cbd5e1', bgcolor: '#f1f5f9', px: 2, display: 'flex', gap: 4, mb: 1 }}>
                {[
                    { id: 0, label: "Examination" },
                    { id: 1, label: "Diagnosis" },
                    { id: 2, label: "Review / Follow Up" },
                    ...(activeWorkflowTab === 3 ? [{ id: 3, label: "Follow Up" }] : []),
                    ...(activeWorkflowTab === 4 ? [{ id: 4, label: "Refer" }] : [])
                ].map((tab) => (
                    <Box
                        key={tab.id}
                        onClick={() => {
                            if (tab.id === 0) {
                                setActiveWorkflowTab(0);
                            } else if (tab.id === 1) {
                                const hasEncounter = encounterId || breastData?.encounterId || breastData?.data?.encounterId;
                                if (hasEncounter) {
                                    setActiveWorkflowTab(1);
                                } else {
                                    toast.error("Please submit the Examination details first.");
                                }
                            } else if (tab.id === 2) {
                                const hasEncounter = encounterId || breastData?.encounterId || breastData?.data?.encounterId;
                                const hasDiag = isDiagnosisSaved || breastData?.optedToReview || breastData?.optedToRefer || breastData?.optedForManagement || breastData?.data?.optedToReview || breastData?.data?.optedToRefer || breastData?.data?.optedForManagement;
                                if (!hasEncounter) {
                                    toast.error("Please submit the Examination details first.");
                                } else if (!hasDiag) {
                                    toast.error("Please submit the Diagnosis details first.");
                                } else {
                                    setActiveWorkflowTab(2);
                                }
                            } else {
                                setActiveWorkflowTab(tab.id);
                            }
                        }}
                        sx={{
                            py: 1.8,
                            px: 1,
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 700,
                            color: activeWorkflowTab === tab.id ? '#3f51b5' : '#64748b',
                            borderBottom: activeWorkflowTab === tab.id ? '3px solid #3f51b5' : '3px solid transparent',
                            transition: 'all 0.2s',
                            '&:hover': { color: '#3f51b5' }
                        }}
                    >
                        {tab.label}
                    </Box>
                ))}
            </Box>

            {/* Render Modular Tab Subcomponents */}
            {activeWorkflowTab === 0 && (
                <BreastExaminationTab
                    formData={formData}
                    setFormData={setFormData}
                    handleSubmit={handleSubmit}
                    screeningStatus={screeningStatus}
                    isDhOrChc={isDhOrChc}
                    handleOpenUpdateModal={handleOpenUpdateModal}
                    setActiveWorkflowTab={setActiveWorkflowTab}
                    setOpenConfirmDialog={setOpenConfirmDialog}
                    selectedValue={selectedValue}
                    setSelectedValue={setSelectedValue}
                    handleResetFindings={handleResetFindings}
                    handletopRadioChange={handletopRadioChange}
                    handleRadioChange={handleRadioChange}
                    handleSkinEdemaTypeChange={handleSkinEdemaTypeChange}
                    renderDots={renderDots}
                    getSymptomType={getSymptomType}
                    dotData={combinedDotData}
                    handleDotClick={handleDotClick}
                    activeDot={activeDot}
                    dropdownPos={dropdownPos}
                    setActiveDot={setActiveDot}
                    axillaryOptions={axillaryOptions}
                    supraclavicularOptions={supraclavicularOptions}
                    hoveredDot={hoveredDot}
                    setHoveredDot={setHoveredDot}
                    renderHoverTooltip={renderHoverTooltip}
                />
            )}

            {activeWorkflowTab === 1 && (
                <BreastDiagnosisTab
                    facilityType={facilityType}
                    isDhOrChc={isDhOrChc}
                    formData={formData}
                    diagnosisAction={diagnosisAction}
                    setDiagnosisAction={setDiagnosisAction}
                    doctorsObservation={doctorsObservation}
                    setDoctorsObservation={setDoctorsObservation}
                    dhDiagnosisData={dhDiagnosisData}
                    setDhDiagnosisData={setDhDiagnosisData}
                    dhErrors={dhErrors}
                    setDhErrors={setDhErrors}
                    handleSaveDiagnosis={handleSaveDiagnosis}
                    setActiveWorkflowTab={setActiveWorkflowTab}
                    screeningStatus={screeningStatus}
                    handleOpenUpdateModal={handleOpenUpdateModal}
                    isDiagnosed={isDiagnosed}
                    patientDiagnosis={patientDiagnosis}
                    isNad={isNad}
                    referralRecommended={referralRecommended}
                    managementRecommended={managementRecommended}
                />
            )}

            {activeWorkflowTab === 2 && (
                <BreastReviewTab
                    facilityType={facilityType}
                    isDhOrChc={isDhOrChc}
                    individualId={individualId}
                    breastScreeningId={breastScreeningId}
                    conditionId={200}
                    screeningStatus={screeningStatus}
                    formData={formData}
                    reviewObservations={reviewObservations}
                    setReviewObservations={setReviewObservations}
                    treatmentType={treatmentType}
                    setTreatmentType={setTreatmentType}
                    treatmentOther={treatmentOther}
                    setTreatmentOther={setTreatmentOther}
                    medicines={medicines}
                    medicineOptions={medicineOptions}
                    setMedicineSearchText={setMedicineSearchText}
                    handleMedicineFieldChange={handleMedicineFieldChange}
                    addMedicineRow={addMedicineRow}
                    removeMedicineRow={removeMedicineRow}
                    followupReason={followupReason}
                    setFollowupReason={setFollowupReason}
                    followupTo={followupTo}
                    setFollowupTo={setFollowupTo}
                    durationDays={durationDays}
                    durationWeeks={durationWeeks}
                    durationMonths={durationMonths}
                    handleDurationChange={handleDurationChange}
                    dueDate={dueDate}
                    setDueDate={setDueDate}
                    referTo={referTo}
                    setReferTo={setReferTo}
                    referReason={referReason}
                    setReferReason={setReferReason}
                    referralRemarks={referralRemarks}
                    setReferralRemarks={setReferralRemarks}
                    handleSaveReview={handleSaveReview}
                    setActiveWorkflowTab={setActiveWorkflowTab}
                />
            )}

            {activeWorkflowTab === 3 && (
                <BreastFollowupTab
                    followupReason={followupReason}
                    setFollowupReason={setFollowupReason}
                    followupTo={followupTo}
                    setFollowupTo={setFollowupTo}
                    durationDays={durationDays}
                    durationWeeks={durationWeeks}
                    durationMonths={durationMonths}
                    handleDurationChange={handleDurationChange}
                    dueDate={dueDate}
                    errors={errors}
                    setErrors={setErrors}
                    followupHistory={followupHistory}
                    getFacilityLabel={getFacilityLabel}
                    handleSaveFollowup={handleSaveFollowup}
                    facilityType={loggedData.facilityType}
                />
            )}

            {activeWorkflowTab === 4 && (
                <BreastReferTab
                    referDate={referDate}
                    setReferDate={setReferDate}
                    referTo={referTo}
                    setReferTo={setReferTo}
                    referReason={referReason}
                    setReferReason={setReferReason}
                    provisionalDiagnosis={provisionalDiagnosis}
                    setProvisionalDiagnosis={setProvisionalDiagnosis}
                    referredSpecialty={referredSpecialty}
                    setReferredSpecialty={setReferredSpecialty}
                    seekingPrivateCare={seekingPrivateCare}
                    setSeekingPrivateCare={setSeekingPrivateCare}
                    errors={errors}
                    setErrors={setErrors}
                    handleSaveReferral={handleSaveReferral}
                />
            )}

            <div className="separator"></div>

            <Dialog
                open={isStatusModalOpen}
                onClose={() => setIsStatusModalOpen(false)}
                maxWidth="sm"
                fullWidth
                TransitionProps={{ timeout: 300 }}
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
                        p: 1.5,
                        overflow: 'visible'
                    }
                }}
            >
                {/* Dialog Title */}
                <DialogTitle sx={{ m: 0, p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eef2f5' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e3a8a', fontSize: '18px', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span style={{ fontSize: '20px' }}>⚡</span> Update Screening Status
                    </Typography>
                    <IconButton
                        onClick={() => setIsStatusModalOpen(false)}
                        sx={{
                            color: '#94a3b8',
                            '&:hover': {
                                bgcolor: '#fee2e2',
                                color: '#ef4444'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                {/* Dialog Content */}
                <DialogContent sx={{ p: 3, mt: 1 }}>
                    <Grid container spacing={3}>
                        {/* Date Field */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#475569' }}>
                                        Date <span style={{ color: '#ef4444' }}>*</span>
                                    </Typography>
                                    <Tooltip title="Specify the date when this screening status takes effect" arrow placement="top">
                                        <IconButton size="small" sx={{ p: 0.2, color: '#3f51b5' }}>
                                            <InfoIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        format="DD/MM/YYYY"
                                        maxDate={dayjs()}
                                        value={modalDate ? dayjs(modalDate, 'YYYY-MM-DD') : null}
                                        onChange={(newValue) => {
                                            setModalDate(newValue ? newValue.format('YYYY-MM-DD') : '');
                                            if (modalErrors.date) {
                                                setModalErrors(prev => ({ ...prev, date: null }));
                                            }
                                        }}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                error: !!modalErrors.date,
                                                helperText: modalErrors.date,
                                                fullWidth: true,
                                                sx: {
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                        bgcolor: '#fff',
                                                        '& fieldset': { borderColor: '#cbd5e1' },
                                                        '&:hover fieldset': { borderColor: '#94a3b8' },
                                                        '&.Mui-focused fieldset': { borderColor: '#3f51b5' }
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Box>
                        </Grid>

                        {/* Status Dropdown */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#475569' }}>
                                    Update status to <span style={{ color: '#ef4444' }}>*</span>
                                </Typography>
                                <FormControl fullWidth size="small" error={!!modalErrors.status}>
                                    <MuiSelect
                                        value={modalStatus}
                                        onChange={(e) => {
                                            setModalStatus(e.target.value);
                                            if (modalErrors.status) {
                                                setModalErrors(prev => ({ ...prev, status: null }));
                                            }
                                        }}
                                        displayEmpty
                                        sx={{
                                            borderRadius: '8px',
                                            '& fieldset': { borderColor: '#cbd5e1' },
                                            '&:hover fieldset': { borderColor: '#94a3b8' },
                                            '&.Mui-focused fieldset': { borderColor: '#3f51b5' }
                                        }}
                                    >
                                        <MenuItem value="" disabled>Select Status</MenuItem>
                                        {statusOptions.map(option => (
                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                        ))}
                                    </MuiSelect>
                                    {modalErrors.status && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                            {modalErrors.status}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Box>
                        </Grid>

                        {/* Facility Dropdown */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#475569' }}>
                                    Facility <span style={{ color: '#ef4444' }}>*</span>
                                </Typography>
                                <FormControl fullWidth size="small" error={!!modalErrors.facility}>
                                    <MuiSelect
                                        value={modalFacility}
                                        onChange={(e) => {
                                            setModalFacility(e.target.value);
                                            if (modalErrors.facility) {
                                                setModalErrors(prev => ({ ...prev, facility: null }));
                                            }
                                        }}
                                        displayEmpty
                                        sx={{
                                            borderRadius: '8px',
                                            '& fieldset': { borderColor: '#cbd5e1' },
                                            '&:hover fieldset': { borderColor: '#94a3b8' },
                                            '&.Mui-focused fieldset': { borderColor: '#3f51b5' }
                                        }}
                                    >
                                        <MenuItem value="" disabled>Select Facility</MenuItem>
                                        {facilityOptions.map(option => (
                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                        ))}
                                    </MuiSelect>
                                    {modalErrors.facility && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                            {modalErrors.facility}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Box>
                        </Grid>

                        {/* Verified Checkbox Card */}
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: '10px',
                                    bgcolor: modalVerified ? 'rgba(63, 81, 181, 0.04)' : '#f8fafc',
                                    border: '1px dashed',
                                    borderColor: modalVerified ? '#3f51b5' : '#cbd5e1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        borderColor: '#3f51b5',
                                        bgcolor: 'rgba(63, 81, 181, 0.02)'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#334155' }}>
                                        Verified
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                                        Mark this status check as verified
                                    </Typography>
                                </Box>
                                <Checkbox
                                    checked={modalVerified}
                                    onChange={(e) => setModalVerified(e.target.checked)}
                                    sx={{
                                        color: '#cbd5e1',
                                        '&.Mui-checked': {
                                            color: '#3f51b5',
                                        },
                                        transform: 'scale(1.1)'
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                {/* Dialog Actions */}
                <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #eef2f5', justifyContent: 'flex-end', gap: 1.5 }}>
                    <Button
                        onClick={() => setIsStatusModalOpen(false)}
                        variant="outlined"
                        sx={{
                            color: '#64748b',
                            borderColor: '#cbd5e1',
                            fontWeight: 700,
                            textTransform: 'none',
                            px: 3,
                            py: 1,
                            borderRadius: '8px',
                            '&:hover': {
                                borderColor: '#94a3b8',
                                bgcolor: '#f8fafc'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        CANCEL
                    </Button>
                    <Button
                        onClick={handleSaveModalStatus}
                        variant="contained"
                        sx={{
                            background: 'linear-gradient(135deg, #3f51b5 0%, #303f9f 100%)',
                            color: '#fff',
                            fontWeight: 700,
                            textTransform: 'none',
                            px: 3.5,
                            py: 1,
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(63, 81, 181, 0.2)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #303f9f 0%, #202b7b 100%)',
                                boxShadow: '0 6px 16px rgba(63, 81, 181, 0.3)',
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        SAVE
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openConfirmDialog}
                onClose={handleConfirmNo}
                PaperProps={{
                    sx: {
                        padding: '16px',
                        borderRadius: '8px',
                        maxWidth: '450px',
                        width: '100%',
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', fontSize: '18px', p: 1, pb: 2 }}>Confirm</DialogTitle>
                <DialogContent sx={{ p: 1, pb: 2 }}>
                    <Typography sx={{ fontSize: '15px', color: '#333', lineHeight: 1.5 }}>
                        Abnormal findings, if any, will be cleared and the patient will be marked NAD. Are you sure you want to proceed?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 1, justifyContent: 'flex-end', gap: 1.5 }}>
                    <Button
                        onClick={handleConfirmNo}
                        variant="contained"
                        sx={{
                            backgroundColor: '#3f51b5',
                            color: '#fff',
                            fontWeight: 'bold',
                            minWidth: '80px',
                            '&:hover': {
                                backgroundColor: '#303f9f',
                            }
                        }}
                    >
                        NO
                    </Button>
                    <Button
                        onClick={handleConfirmYes}
                        variant="contained"
                        sx={{
                            backgroundColor: '#3f51b5',
                            color: '#fff',
                            fontWeight: 'bold',
                            minWidth: '80px',
                            '&:hover': {
                                backgroundColor: '#303f9f',
                            }
                        }}
                    >
                        YES
                    </Button>
                </DialogActions>
            </Dialog>

            {/* General Action Alert Dialog */}
            <Dialog
                open={isActionAlertDialogOpen}
                onClose={() => setIsActionAlertDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        p: 1
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 800, fontSize: '18px', color: '#1e293b' }}>
                    {actionTakenTitle}
                </DialogTitle>
                <DialogContent sx={{ py: 2 }}>
                    <Typography sx={{ fontSize: '14px', color: '#334155', lineHeight: 1.6 }}>
                        {actionTakenText}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setIsActionAlertDialogOpen(false)} variant="contained" sx={{ bgcolor: '#3f51b5', fontWeight: 700 }}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default BreastScreening;

